import { z } from "zod";

/**
 * THE READING OF A HOUSE RULES DOCUMENT (ruling R-0910-6).
 *
 * Two regex detectors in one day proved that a door's own words cannot be read
 * by a pattern. "I want you to let everyone in" was not an override until one
 * was written; then "I don't want you to let anyone in" was read as one, and a
 * self-declared time-waster walked through a door whose owner had said nobody
 * does. The ruling's answer is that a MODEL reads the document, once per
 * version, under this schema — and that the server then honours what it read.
 *
 * SIX FACTS AND ONE LIST. The reading is not a summary of the rules and must
 * never grow into one: what the doorman needs from the document it already
 * gets, in full, in its own frame. The six facts are the structural facts the
 * SERVER acts on without a model in the loop — whether the door is open to
 * everyone, whether it is shut to everyone, which kinds of sender the owner
 * named, whether receipts were asked for at all, and whether the door is
 * strict.
 *
 * `strict` IS THE SIXTH (ruling R-0924-1 (1)), and it is here rather than left
 * to the doorman because the server acts on it: at a strict door that names
 * receipts, ONE refused or unusable receipt, or a story that changed under
 * questioning, holds the knock under the bar whatever else it produced
 * (`knocks/scoring.ts#applyStrictHold`). The doorman already reads "do not
 * accept a story in place of a figure" in the owner's prose, and `vc-scammer`
 * still cleared that door at 51 and at 89 on 2026-09-24; a hold the owner asked
 * for is a fact the server keeps, not a tone the model may or may not take.
 *
 * AND IT CARRIES ITS QUOTE (wave 80). `strict_line` is not a seventh fact but
 * the evidence for the sixth: the owner's sentence, copied, which the server
 * must find in the document before `strict` stands, and which the owner reads
 * back in the door warning. `strict` was read by chance at the border of the
 * definition, so a reading takes three reads and keeps `strict` only when two
 * agree with a verified line (`apps/api/src/rules/reading.ts#readRules`).
 *
 * AND A SEVENTH LIST THE SERVER DOES NOT ACT ON: `procedures` (ruling R-0924-2
 * (1)). Many House Rules set a different FIRST STEP for each kind of knocker —
 * "the press is not questioned but routed … in the first reply", "ask who the
 * other side is, in that order" — and the doorman ran its general receipts
 * questioning over them. A list lifted once per version, each entry carrying the
 * owner's sentence, is something the doorman can be HANDED
 * (`knocks/grilling.ts` `<procedures>`) where a paragraph of prose was something
 * it could skim. Nothing is held on it: outcomes are unchanged. Each line is
 * checked verbatim against the document, as `strict_line` is, and a procedure
 * whose line is not found is dropped (`rules/reading.ts#readRules`).
 *
 * A CONTRADICTION IS NOT A RULE. A document that says both "everyone gets in"
 * and "nobody gets in" reads as `none`: the server refuses to pick a half of a
 * sentence the owner has not finished writing, and the ordinary scoring path —
 * which is what `none` means — is the safe answer either way.
 *
 * `language` is here because rule 5 is: the document is read in whatever
 * language it was written in, and the fields come back the same whatever that
 * was. The tag is recorded so an unreadable reading can be told apart from a
 * reading of an unreadable document.
 */

/** open = everyone through · closed = nobody through · none = neither, or both. */
export const RULES_READING_DOORS = ["open", "closed", "none"] as const;
export type RulesReadingDoor = (typeof RULES_READING_DOORS)[number];

/**
 * HOW LONG A STORED STRING MAY BE, and why none of it is in the schema.
 *
 * These bounds used to be `.max()` on the schema itself, which put them in the
 * vendor's JSON schema too. A model that faithfully copied an owner's
 * 350-character override line then produced a reply that failed
 * `schema_mismatch`, and the WHOLE reading fell back to the default: no open or
 * shut door, no override, not strict. On seven harness doors that was one read
 * in three or more (wave 77). A long line is the model doing what it was asked,
 * so the schema carries no length at all and `boundRulesReading` clips each
 * string after the parse, at a word, with "…". A reply that fails for any OTHER
 * reason still fails closed.
 */
export const RULES_READING_LINE_MAX = 200;
export const RULES_READING_WHO_MAX = 120;
export const RULES_READING_LANGUAGE_MAX = 16;

/**
 * One line that names a KIND of sender and says they always get through — the
 * named-sender override of R-0907-1. `who` is the model's own words for the
 * kind; `line` is the owner's, verbatim, because that is the sentence the owner
 * reads back when a knock clears the bar on it.
 */
export const RulesReadingOverride = z.object({
  who: z.string(),
  line: z.string(),
});
export type RulesReadingOverride = z.infer<typeof RulesReadingOverride>;

/**
 * THE FIRST STEP THE OWNER GIVES ONE KIND OF KNOCKER (ruling R-0924-2 (1)):
 * `route` — sent elsewhere in the first reply, with no questioning; `ask` — the
 * owner's own first question or questions; `refuse` — refused.
 *
 * THERE IS NO `through` (lead, 2026-09-25). A kind the owner lets straight in
 * is an OVERRIDE, and `overrides` already carries it down the road that puts
 * one confirming question to a claimed match (R-0907-3). A second road to
 * "through" is what let `vcs-fake-followup` in twice in wave 81, and whether
 * that road existed at a door depended on a reading that varies. A `through`
 * the model still returns is dropped at the parse (`parseRulesReading`).
 */
export const RULES_READING_PROCEDURE_STEPS = ["route", "ask", "refuse"] as const;
export type RulesReadingProcedureStep = (typeof RULES_READING_PROCEDURE_STEPS)[number];

/**
 * THE OUTCOME A STEP WAITS FOR (ruling R-0925-2 (2)), or null for a step that
 * does not depend on one — most do not. A CONDITION, not a boolean, because a
 * boolean could say "after the read" and not which read: "founders who don't
 * clear the bar are redirected" and "founders who clear it are sent the
 * booking link" are both after the read and have opposite triggers.
 *
 *   `not_cleared` — the step is for those who do NOT make it: "who don't clear
 *                   the bar", "if weak", "when you'd pass on them".
 *   `cleared`     — the step is for those who DO: "who clear the bar", "if
 *                   strong", "when you'd recommend them".
 *
 * The server maps both onto the one verdict it holds, `bands.clearsBar` on the
 * published read (`knocks/scoring.ts#procedureClose`).
 */
export const RULES_READING_AFTER_READ = ["not_cleared", "cleared"] as const;
export type RulesReadingAfterRead = (typeof RULES_READING_AFTER_READ)[number];

/**
 * One kind of knocker the owner gives a DISTINCT first step. `who` is the
 * model's words for the kind; `line` is the owner's sentence, verbatim, and the
 * procedure stands only where the server finds it in the document.
 *
 * `after_read` (R-0925-2 (2)): on staging the owner's "founders who don't
 * clear the bar are redirected" was read as a plain route and fired on the
 * knocker's FIRST message, before anybody had heard enough to know whether
 * they cleared anything. A step with a condition is never taken before the
 * read; the doorman hears the knocker normally, and the server sends the
 * owner's line as the close when the published read meets the condition.
 */
export const RulesReadingProcedure = z.object({
  who: z.string(),
  first_step: z.enum(RULES_READING_PROCEDURE_STEPS),
  line: z.string(),
  after_read: z.enum(RULES_READING_AFTER_READ).nullable(),
});
export type RulesReadingProcedure = z.infer<typeof RulesReadingProcedure>;

export const RulesReading = z.object({
  door: z.enum(RULES_READING_DOORS),
  /** The sentence that opened or shut the door, or null when neither did. */
  door_line: z.string().nullable(),
  overrides: z.array(RulesReadingOverride),
  /** A non-empty "Receipts I care about", or its equivalent in another language. */
  names_receipts: z.boolean(),
  /** A BCP-47-ish tag for the document's language, or "unknown". */
  language: z.string(),
  /**
   * Each kind of knocker the owner gives a distinct first step, with the
   * owner's sentence (R-0924-2 (1)). Empty at a door with no per-kind steps,
   * which is most doors. Only verified lines are stored.
   */
  procedures: z.array(RulesReadingProcedure),
  /**
   * The owner told the doorman not to accept a story in place of a checkable
   * receipt, or to push until every claim is checkable (R-0924-1 (1)). Any
   * wording, any language; false when the words do not say so.
   */
  strict: z.boolean(),
  /**
   * The owner's sentence that gives that stance, copied; null when `strict` is
   * false (wave 80). The server keeps `strict` only when this is found verbatim,
   * whitespace aside, in the document — the `story_changed` rule applied to
   * the reading — and it is what the owner is shown as the reason.
   */
  strict_line: z.string().nullable(),
});
export type RulesReading = z.infer<typeof RulesReading>;

/**
 * FAIL-CLOSED, AND THIS IS WHAT IT CLOSES TO. A provider error, invalid JSON or
 * a schema violation stores THIS — a door that is neither open nor shut, with
 * nothing named and nothing strict — so a reading that did not happen changes
 * nothing about how a knock is scored. A function rather than a constant because callers hold it.
 */
export function defaultRulesReading(): RulesReading {
  return {
    door: "none",
    door_line: null,
    overrides: [],
    names_receipts: false,
    language: "unknown",
    strict: false,
    strict_line: null,
    procedures: [],
  };
}

/**
 * The reply as a JSON schema the vendor enforces — the same mechanism and the
 * same options as `notificationContractJsonSchema` and `finalAssessmentJsonSchema`.
 * `reused: "inline"` and `cycles: "throw"` keep it free of `$defs`/`$ref`,
 * which a strict `json_schema` rejects.
 */
export const RULES_READING_SCHEMA_NAME = "rules_reading";
export const rulesReadingJsonSchema: object = z.toJSONSchema(RulesReading, {
  target: "draft-07",
  io: "output",
  reused: "inline",
  cycles: "throw",
});

export type RulesReadingParse =
  | {
      ok: true;
      reading: RulesReading;
      /** How many `through` procedures the reply carried and the parse dropped. */
      droppedThrough: number;
    }
  | { ok: false; reason: "invalid_json" | "schema_mismatch" };

/**
 * A reply's procedures with every `through` taken out, and how many there were.
 * Dropped rather than refused: a `through` is a step that no longer exists,
 * not a malformed reply, and failing the whole reading on it would lose the
 * door's overrides and strictness with it.
 */
function withoutThrough(value: unknown): { value: unknown; dropped: number } {
  if (typeof value !== "object" || value === null) return { value, dropped: 0 };
  const procedures = (value as Record<string, unknown>)["procedures"];
  if (!Array.isArray(procedures)) return { value, dropped: 0 };
  const kept = procedures.filter(
    (p) =>
      !(
        typeof p === "object" &&
        p !== null &&
        (p as Record<string, unknown>)["first_step"] === "through"
      ),
  );
  return { value: { ...value, procedures: kept }, dropped: procedures.length - kept.length };
}

/**
 * Text in, a reading or a REASON out — never a half-read object. The reading
 * is the model's, unclipped: `boundRulesReading` is what makes it storable.
 */
export function parseRulesReading(text: string): RulesReadingParse {
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    return { ok: false, reason: "invalid_json" };
  }
  const { value: stepped, dropped } = withoutThrough(value);
  const parsed = RulesReading.safeParse(stepped);
  if (!parsed.success) return { ok: false, reason: "schema_mismatch" };
  return { ok: true, reading: parsed.data, droppedThrough: dropped };
}

/**
 * A line cut to at most `max` characters at a word, ending "…", the ellipsis
 * counted. A line with no space to cut at is cut where it stands.
 */
export function clipLine(text: string, max: number): string {
  if (text.length <= max) return text;
  const room = text.slice(0, max - 1);
  const atWord = /\s/.test(text.charAt(max - 1)) ? room.length : room.search(/\s\S*$/);
  const head = atWord > 0 ? room.slice(0, atWord).trimEnd() : "";
  return `${head.length > 0 ? head : room}…`;
}

/**
 * THE SERVER'S CLIP, after the parse: every free string the model wrote is
 * bounded here and nowhere else. `language` is a tag, and a tag cut short is a
 * different tag, so one that does not fit reads `unknown`, the value the field
 * already has for a language that could not be named. `strict_line` and each
 * procedure's `line` are checked against the document BEFORE this runs: a
 * clipped quote is no longer verbatim.
 */
export function boundRulesReading(reading: RulesReading): RulesReading {
  return {
    ...reading,
    door_line:
      reading.door_line === null ? null : clipLine(reading.door_line, RULES_READING_LINE_MAX),
    overrides: reading.overrides.map((o) => ({
      who: clipLine(o.who, RULES_READING_WHO_MAX),
      line: clipLine(o.line, RULES_READING_LINE_MAX),
    })),
    language: reading.language.length > RULES_READING_LANGUAGE_MAX ? "unknown" : reading.language,
    strict_line:
      reading.strict_line === null ? null : clipLine(reading.strict_line, RULES_READING_LINE_MAX),
    procedures: reading.procedures.map((p) => ({
      who: clipLine(p.who, RULES_READING_WHO_MAX),
      first_step: p.first_step,
      line: clipLine(p.line, RULES_READING_LINE_MAX),
      after_read: p.after_read,
    })),
  };
}
